import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { getCompletionColors, getThemeColors } from '../utils/themeUtils';
import type { CalendarDay, Theme } from '../types';

interface CalendarProps {
  selectedMonth: Date;
  calendarData: CalendarDay[];
  isDark: boolean;
  onMonthChange: (date: Date) => void;
  onDateSelect?: (date: Date) => void;
  theme: Theme;
}

export function Calendar({ selectedMonth, calendarData, isDark, onMonthChange, onDateSelect, theme }: CalendarProps) {
  const themeColors = getThemeColors(theme);

  const prevMonth = () => {
    const date = new Date(selectedMonth);
    date.setMonth(date.getMonth() - 1);
    onMonthChange(date);
  };

  const nextMonth = () => {
    const date = new Date(selectedMonth);
    date.setMonth(date.getMonth() + 1);
    onMonthChange(date);
  };

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const days = new Date(year, month + 1, 0).getDate();
    const firstDay = new Date(year, month, 1).getDay();
    return { days, firstDay };
  };

  const { days, firstDay } = getDaysInMonth(selectedMonth);
  const today = new Date().toISOString().split('T')[0];

  const handleDateClick = (day: number) => {
    if (onDateSelect) {
      const clickedDate = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth(), day);
      onDateSelect(clickedDate);
    }
  };

  return (
    <div className={`rounded-xl shadow-lg ${isDark ? 'bg-gray-800' : 'bg-white'} p-4 sm:p-6 mb-6 sm:mb-8`}>
      <div className="flex justify-between items-center mb-4">
        <button 
          onClick={prevMonth} 
          className={`p-2 hover:${isDark ? 'bg-gray-700' : 'bg-gray-100'} rounded-lg transition-colors`}
        >
          <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
        </button>
        <h2 className="text-base sm:text-lg font-semibold">
          {selectedMonth.toLocaleString('default', { month: 'long', year: 'numeric' })}
        </h2>
        <button 
          onClick={nextMonth} 
          className={`p-2 hover:${isDark ? 'bg-gray-700' : 'bg-gray-100'} rounded-lg transition-colors`}
        >
          <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <div key={day} className="text-center text-xs sm:text-sm font-medium py-2">
            <span className="hidden sm:inline">{day}</span>
            <span className="sm:hidden">{day.charAt(0)}</span>
          </div>
        ))}
        
        {Array.from({ length: firstDay }).map((_, i) => (
          <div key={`empty-${i}`} className="aspect-square" />
        ))}
        
        {Array.from({ length: days }).map((_, i) => {
          const date = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth(), i + 1)
            .toISOString()
            .split('T')[0];
          const dayData = calendarData.find(d => d.date === date);
          const isToday = date === today;
          const dayNumber = i + 1;

          return (
            <div
              key={date}
              className={`aspect-square p-1 ${isToday ? 'rounded-lg' : ''}`}
              style={isToday ? { backgroundColor: themeColors[100] } : {}}
            >
              <div
                className={`w-full h-full rounded-lg flex items-center justify-center text-xs sm:text-sm cursor-pointer transition-all hover:scale-105 ${
                  !dayData && (isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100')
                } ${isToday ? 'font-bold ring-2' : ''}`}
                style={{
                  ...(dayData ? getCompletionColors(theme, dayData.completionRate) : {}),
                  ...(isToday ? { '--tw-ring-color': themeColors[500] } : {})
                }}
                onClick={() => handleDateClick(dayNumber)}
                title={dayData ? `${Math.round(dayData.completionRate * 100)}% completed` : 'No prayers recorded'}
              >
                {dayNumber}
              </div>
            </div>
          );
        })}
      </div>
      
      {/* Legend */}
      <div className="mt-4 flex flex-wrap items-center justify-center gap-2 text-xs">
        <span className={`${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Prayer completion:</span>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-gray-200 rounded"></div>
          <span>0%</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded" style={getCompletionColors(theme, 0.3)}></div>
          <span>20-40%</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded" style={getCompletionColors(theme, 0.7)}></div>
          <span>60-80%</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded" style={getCompletionColors(theme, 1.0)}></div>
          <span>100%</span>
        </div>
      </div>
    </div>
  );
}